import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, takeUntil } from 'rxjs/operators';
import {
  loadApiMetadataPipelineRunSummary,
  loadApiMetadataValidatorGroupDetails,
  loadApiMetadataValidatorGroupDetailsError,
  loadApiMetadataValidatorGroupSummary,
  loadApiMetadataValidatorGroupSummaryError,
  loadApiMetadataValidatorGroupSummaryNotFound,
  loadApiMetadataValidatorPipelineRunSummaryError,
  loadApiMetadataValidatorPipelineRunSummaryNotFound,
  setApiMetadataValidatorGroupDetails,
  setApiMetadataValidatorGroupSummary,
  setApiMetadataValidatorPipelineRunSummary,
} from './api-metadata-validator.actions';
import { ApiMetadataValidatorDetail, ApiMetadataValidatorSummary } from '../../model/api-metadata-validator';
import { AccessGroupService } from '../../../shared/access-api/access-group.service';
import { AccessPipelineRunService } from '../../../shared/access-api/access-pipeline-run.service';

@Injectable()
export class ApiMetadataValidatorEffects {
  private readonly action$ = inject(Actions);
  private readonly accessApiGroupService = inject(AccessGroupService);
  private readonly accessApiPipelineRunService = inject(AccessPipelineRunService);

  public onLoadGroupSummary$ = createEffect(() => {
    return this.action$.pipe(
      ofType(loadApiMetadataValidatorGroupSummary),
      switchMap((action) =>
        this.accessApiGroupService.getApiMetadataValidatorGroupSummary(action.deliveryId, action.groupComplianceMode).pipe(
          map((groupSummary: ApiMetadataValidatorSummary) => setApiMetadataValidatorGroupSummary({ groupSummary })),
          catchError((error: HttpErrorResponse) => {
            const deliveryId = action.deliveryId;
            if (error.status === 404) {
              return of(loadApiMetadataValidatorGroupSummaryNotFound({ deliveryId }));
            }
            return of(loadApiMetadataValidatorGroupSummaryError({ deliveryId }));
          }),
        ),
      ),
    );
  });

  public onLoadGroupDetails$ = createEffect(() => {
    return this.action$.pipe(
      ofType(loadApiMetadataValidatorGroupDetails),
      switchMap((action) =>
        this.accessApiGroupService.getApiMetadataValidatorGroupDetails(action.deliveryId, action.groupComplianceMode).pipe(
          map((groupDetails: ApiMetadataValidatorDetail[]) => setApiMetadataValidatorGroupDetails({ groupDetails })),
          catchError(() => {
            return of(loadApiMetadataValidatorGroupDetailsError({ deliveryId: action.deliveryId }));
          }),
        ),
      ),
    );
  });

  public onLoadSingleDetails$ = createEffect(() => {
    return this.action$.pipe(
      ofType(loadApiMetadataPipelineRunSummary),
      mergeMap((action) =>
        this.accessApiPipelineRunService.getPipelineRunApiMetadataValidatorInfo(action.pipelineRunId).pipe(
          map((apiMetadataValidatorDetails: ApiMetadataValidatorDetail) =>
            setApiMetadataValidatorPipelineRunSummary({
              pipelineRunId: action.pipelineRunId,
              pipelineRunSummary: apiMetadataValidatorDetails,
            }),
          ),
          catchError((error: HttpErrorResponse) => {
            if (error.status === 404) {
              return of(loadApiMetadataValidatorPipelineRunSummaryNotFound({ pipelineRunId: action.pipelineRunId }));
            }
            return of(loadApiMetadataValidatorPipelineRunSummaryError({ pipelineRunId: action.pipelineRunId }));
          }),
          takeUntil(
            this.action$.pipe(
              ofType(loadApiMetadataPipelineRunSummary),
              filter((newAction) => newAction.pipelineRunId === action.pipelineRunId),
            ),
          ),
        ),
      ),
    );
  });
}
